import type { RuleTopic } from '../vacationHelp.types';
import { MIN_CORRECTION_LEAD_DAYS } from '@/features/Vacations/utils/vacationStartDateRules';
import { ENTITLED_DAYS_PER_PERIOD, SELL_DAYS } from '@/features/Vacations/utils/vacationRules';
import { EXPIRING_WINDOW_DAYS } from '@/features/Vacations/utils/panorama/constants';

export const MANAGEMENT_RULES: RuleTopic[] = [
    {
        id: 'filters',
        icon: '🎚️',
        title: 'Cartões e filtros',
        summary: 'Como navegar o painel de gestão.',
        content: (
            <div className="space-y-3 text-sm">
                <p>Os cartões no topo filtram os pedidos. Clique em um para ativar; clique de novo para desativar.</p>
                <p><strong>Mês e ano:</strong> só aparecem com "Em Férias", "Agendadas" ou "Férias concluídas".</p>
                <p><strong>Cliente:</strong> limita a um único cliente.</p>
                <p className="text-gray-600 text-xs mt-2">
                    Busque por nome ou e-mail. Clique na coluna para ordenar. Use "Colunas" para escolher quais aparecem.
                </p>
            </div>
        ),
    },
    {
        id: 'deciding',
        icon: '✍️',
        title: 'Decidir um pedido',
        summary: 'Validar, reprovar ou pedir correção de datas.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>Validar / Reprovar:</strong> se há 2 períodos, você decide cada um separadamente.</p>
                <p><strong>Motivo (obrigatório):</strong> ao reprovar, deixe uma nota com mínimo 10 caracteres. Fica registrado na linha do tempo do pedido.</p>
                <p><strong>Abono:</strong> só aparece com pelo menos 1 período validado. Você aprova ou reprova o abono de {SELL_DAYS} dias.</p>
                <p><strong>Resultado:</strong> se todos validados → Agendada; todos reprovados → Reprovada; misto → Necessário correção (colaborador escolhe novas datas).</p>
                <p className="text-gray-600 text-xs mt-2">
                    Correção: nova data com mínimo {MIN_CORRECTION_LEAD_DAYS} dias de antecedência. Precisa de outra pessoa do Departamento Pessoal para validar o próprio pedido.
                </p>
            </div>
        ),
        facts: [
            { label: 'Motivo mín.', value: '10 caracteres' },
            { label: 'Para corrigir', value: `≥ ${MIN_CORRECTION_LEAD_DAYS} dias` },
            { label: 'Abono dias', value: `${SELL_DAYS}` },
        ],
    },
    {
        id: 'risk',
        icon: '⚠️',
        title: 'Prazos e vencimentos',
        summary: 'Datas importantes que você acompanha.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>A vencer em 30 dias:</strong> o limite para agendar férias vence em até 30 dias. Depois disso, os dias expiram.</p>
                <p><strong>Vencido:</strong> período passou do prazo (art. 137 CLT). Gera pagamento em dobro se não for gozado na rescisão.</p>
                <p><strong>Limite p/ agendar até:</strong> última data para começar férias. Não é data de pedido; é data de início.</p>
                <p className="text-gray-600 text-xs mt-2">
                    O limite de 11 meses é regra da empresa. A lei marca o vencimento em 12 meses.
                </p>
            </div>
        ),
        facts: [
            { label: 'Alerta vencimento', value: '30 dias' },
            { label: 'Dias por período', value: `${ENTITLED_DAYS_PER_PERIOD}` },
            { label: 'Dobro na rescisão', value: 'Sim' },
        ],
    },
    {
        id: 'holidays',
        icon: '🎄',
        title: 'Feriados',
        summary: 'Gerencie o calendário de feriados.',
        content: (
            <div className="space-y-3 text-sm">
                <p>Os feriados nacionais de data fixa e os de BH já vêm prontos e não podem ser editados.</p>
                <p><strong>Adicionar:</strong> novo feriado ou ponto facultativo para todos.</p>
                <p><strong>Duplicar:</strong> copia os cadastrados do ano exibido para o próximo ano. Datas móveis (Carnaval, Sexta-feira Santa) precisam de ajuste depois.</p>
                <p><strong>Bloqueio:</strong> colaboradores não podem começar férias no feriado ou nos 2 dias antes dele.</p>
                <p className="text-gray-600 text-xs mt-2">
                    Mudanças valem imediatamente. Todos veem o calendário atualizado.
                </p>
            </div>
        ),
    },
    {
        id: 'emails',
        icon: '📧',
        title: 'E-mails do sistema',
        summary: 'Notificações automáticas que são enviadas.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>Ao colaborador:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
                    <li>Pedido aberto (gestor recebeu)</li>
                    <li>Aprovado/reprovado pelo gestor e cliente</li>
                    <li>Validação pendente - DP</li>
                    <li>Férias agendada</li>
                    <li>Necessário correção de período</li>
                    <li>"Suas férias começam" (7 dias e 1 dia antes)</li>
                    <li>Férias concluída</li>
                </ul>
                <p className="mt-2"><strong>Ao gestor:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
                    <li>Novo pedido esperando aprovação</li>
                    <li>Lembretes: 60, 30 e 15 dias antes do prazo, depois diariamente</li>
                </ul>
            </div>
        ),
    },
];
